/**
 * Swamithra — the attachment rules, and the one place a file is checked before
 * it is sent.
 *
 * THREE PLATFORMS, ONE RULE SET. A file picked from a browser, from an Expo
 * document picker and from an Expo camera capture arrive as three different
 * shapes; the limit that applies to them is the same, and US-00-002.2 requires
 * the rejection message to "name the specific rule broken" identically on all
 * three.
 *
 * THE CLIENT CHECK IS NOT THE ENFORCEMENT. The server sniffs the content type
 * and returns 422 for a spoofed extension (US-00-002.2, US-00-004.3). What this
 * module buys is a specific message in under a millisecond instead of a generic
 * one after a 5 MB upload — and, through `sniffContentType`, the chance to catch
 * a renamed .docx before the user waits for that upload at all.
 */

import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS_PER_SUBMISSION } from "../domain/constants"

// -----------------------------------------------------------------------------
// The cross-platform file
// -----------------------------------------------------------------------------

/**
 * A file chosen by a user, in the one shape this workspace passes around.
 *
 * `source` is the platform's own handle:
 *   - web: the `File` from an `<input type="file">` or a drop, which is a Blob
 *   - native: `{ uri }` from expo-image-picker or expo-document-picker
 *
 * React Native's `FormData` accepts the `{ uri, name, type }` object directly
 * and its `Blob` cannot be constructed from a local file path, which is why this
 * is a union rather than everything being coerced to a Blob.
 */
export type PlatformFile = {
	name: string
	/** As reported by the picker. Treated as a CLAIM, never as a fact. */
	type: string
	sizeBytes: number
	source: Blob | { uri: string }
}

// -----------------------------------------------------------------------------
// The rule sets
// -----------------------------------------------------------------------------

/**
 * Query attachments, follow-up attachments and the vetting NOTICE.
 *
 * DOCX is deliberately absent: it is accepted only on the vetting draft response
 * (US-00-002.24).
 */
export const DOCUMENT_IMAGE_TYPES: readonly string[] = [
	"application/pdf",
	"image/jpeg",
	"image/png",
]

/** The vetting DRAFT, which additionally accepts a Word document (US-00-004.3). */
export const DRAFT_TYPES: readonly string[] = [
	...DOCUMENT_IMAGE_TYPES,
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/msword",
]

const EXTENSIONS: Record<string, string> = {
	"application/pdf": "PDF",
	"image/jpeg": "JPG",
	"image/png": "PNG",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
	"application/msword": "DOC",
}

/** `PDF, JPG or PNG` — the phrasing used in every rejection message. */
export function describeTypes(types: readonly string[]): string {
	const names = types.map(type => EXTENSIONS[type] ?? type)
	if (names.length <= 1) return names[0] ?? ""
	return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1] ?? ""}`
}

export type FileRules = {
	accepted: readonly string[]
	maxBytes: number
	maxFiles: number
}

/** Query attachments and follow-up attachments: up to three, PDF/JPG/PNG, 5 MB. */
export const QUERY_ATTACHMENT_RULES: FileRules = {
	accepted: DOCUMENT_IMAGE_TYPES,
	maxBytes: MAX_ATTACHMENT_BYTES,
	maxFiles: MAX_ATTACHMENTS_PER_SUBMISSION,
}

/** The vetting notice: exactly one, PDF/JPG/PNG, 5 MB. */
export const VETTING_NOTICE_RULES: FileRules = {
	accepted: DOCUMENT_IMAGE_TYPES,
	maxBytes: MAX_ATTACHMENT_BYTES,
	maxFiles: 1,
}

/** The vetting draft: exactly one, PDF/DOCX/JPG/PNG, 5 MB. */
export const VETTING_DRAFT_RULES: FileRules = {
	accepted: DRAFT_TYPES,
	maxBytes: MAX_ATTACHMENT_BYTES,
	maxFiles: 1,
}

// -----------------------------------------------------------------------------
// Validation
// -----------------------------------------------------------------------------

export type FileRejection =
	| { reason: "too_large"; message: string }
	| { reason: "unsupported_type"; message: string }
	| { reason: "too_many"; message: string }

/**
 * `5 MB`, `512 KB` — for a message a user can act on.
 *
 * Binary units, matching MAX_ATTACHMENT_BYTES and the server: the limit has to
 * READ as "5 MB" in the rejection message, and 5 × 1024 × 1024 divided by a
 * million reads as "5.2 MB", which is a limit nobody was told about.
 */
export function formatBytes(bytes: number): string {
	if (bytes >= 1024 * 1024) {
		const mb = bytes / (1024 * 1024)
		return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
	}
	return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Checks ONE file against a rule set.
 *
 * Returns the rejection rather than throwing, and names the rule that was broken
 * — "That file is 7.2 MB. Each file must be 5 MB or smaller." rather than
 * "Invalid file" (US-00-002.2, US-00-004.3).
 */
export function validateFile(file: PlatformFile, rules: FileRules): FileRejection | null {
	if (!isAcceptedType(file, rules.accepted)) {
		return {
			reason: "unsupported_type",
			message: `${file.name} is not a supported format. Attach a ${describeTypes(rules.accepted)} file.`,
		}
	}
	if (file.sizeBytes > rules.maxBytes) {
		return {
			reason: "too_large",
			message: `${file.name} is ${formatBytes(file.sizeBytes)}. Each file must be ${formatBytes(rules.maxBytes)} or smaller.`,
		}
	}
	return null
}

/**
 * The claimed type, falling back to the extension.
 *
 * Some Android document pickers report `application/octet-stream` for a file the
 * user genuinely picked from Downloads. Rejecting those on the claim alone means
 * a user with a real PDF cannot attach it, so the extension is consulted second
 * — and the server's content sniffing is what actually decides.
 */
function isAcceptedType(file: PlatformFile, accepted: readonly string[]): boolean {
	const claimed = file.type.toLowerCase().split(";")[0]?.trim() ?? ""
	if (accepted.includes(claimed)) return true

	const guessed = typeFromFileName(file.name)
	return guessed !== null && accepted.includes(guessed)
}

/** Content type from a file name, for pickers that do not report one. */
export function typeFromFileName(fileName: string): string | null {
	const lower = fileName.toLowerCase()
	if (lower.endsWith(".pdf")) return "application/pdf"
	if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg"
	if (lower.endsWith(".png")) return "image/png"
	if (lower.endsWith(".docx")) {
		return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
	}
	if (lower.endsWith(".doc")) return "application/msword"
	return null
}

export type FileSetResult = {
	/** The files that may be sent, in the order they were offered. */
	accepted: PlatformFile[]
	/** One rejection per rejected file, in the same order. */
	rejections: FileRejection[]
}

/**
 * Checks a whole selection, KEEPING the files that pass.
 *
 * US-00-002.2 is explicit that the other selected files are retained when one is
 * rejected. Returning a partitioned result rather than throwing on the first bad
 * file is what makes that the default behaviour rather than something each
 * screen has to remember.
 *
 * `alreadyAttached` is the count already on the composer, so the fourth file is
 * rejected as the fourth even when it arrives in its own picker session.
 */
export function validateFileSet(
	files: readonly PlatformFile[],
	rules: FileRules,
	alreadyAttached = 0,
): FileSetResult {
	const accepted: PlatformFile[] = []
	const rejections: FileRejection[] = []

	for (const file of files) {
		if (alreadyAttached + accepted.length >= rules.maxFiles) {
			rejections.push({
				reason: "too_many",
				message:
					rules.maxFiles === 1
						? `Only one file can be attached here. ${file.name} was not added.`
						: `You can attach up to ${rules.maxFiles} files. ${file.name} was not added.`,
			})
			continue
		}
		const rejection = validateFile(file, rules)
		if (rejection) {
			rejections.push(rejection)
			continue
		}
		accepted.push(file)
	}

	return { accepted, rejections }
}

// -----------------------------------------------------------------------------
// Content sniffing
// -----------------------------------------------------------------------------

/**
 * The real type of a file, from its first bytes.
 *
 * The same check the server performs, run client-side where the bytes are
 * already in hand — a renamed `.docx` submitted as a notice is then rejected in
 * the picker rather than after a 5 MB upload and a 422 (US-00-004.3).
 *
 * DOCX and any other OOXML file are both ZIP archives and are indistinguishable
 * from their first four bytes, so a ZIP is reported as DOCX. That is the right
 * direction to be wrong in: the only place DOCX is accepted is the vetting
 * draft, where the server does the real inspection.
 *
 * Returns null when the bytes match nothing known — the caller then falls back
 * to the claimed type rather than blocking a file it simply does not recognise.
 */
export function sniffContentType(bytes: Uint8Array): string | null {
	if (startsWith(bytes, [0x25, 0x50, 0x44, 0x46])) return "application/pdf" // %PDF
	if (startsWith(bytes, [0xff, 0xd8, 0xff])) return "image/jpeg"
	if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png"
	if (
		startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]) ||
		startsWith(bytes, [0x50, 0x4b, 0x05, 0x06])
	) {
		return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
	}
	if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
		return "application/msword"
	}
	return null
}

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
	if (bytes.length < signature.length) return false
	return signature.every((byte, index) => bytes[index] === byte)
}

/** Bytes enough to identify any signature above. */
export const SNIFF_BYTE_COUNT = 8

// -----------------------------------------------------------------------------
// Sending
// -----------------------------------------------------------------------------

/**
 * Appends files to a multipart body under one field name.
 *
 * THE CAST IS LOAD-BEARING AND IS NOT AN ESCAPE HATCH. React Native's FormData
 * takes `{ uri, name, type }` and turns it into a file part by reading the URI
 * natively; the DOM's typings only know about `Blob`. There is no shape that
 * satisfies both type systems, so the native object is passed through as
 * `unknown` with this comment attached rather than the whole upload path being
 * typed loosely.
 */
export function appendFiles(form: FormData, field: string, files: readonly PlatformFile[]): void {
	for (const file of files) {
		if (file.source instanceof Blob) {
			form.append(field, file.source, file.name)
			continue
		}
		form.append(field, {
			uri: file.source.uri,
			name: file.name,
			type: file.type,
		} as unknown as Blob)
	}
}
